<template>
  <div v-if="object && !status" class="product-content flex-column">
    <RepBanner />
    <div class="product-detail">
      <div
        v-if="route.query.back"
        class="back-row flex-row align-center flex-align-center m-sm-b"
      >
        <RouterLink :to="route.query.back" class="flex-row gap-2xs">
          <i class="pi pi-chevron-left" />
          <span>Back to search</span>
        </RouterLink>
      </div>
      <div class="flex-row">
        <ImageGallery
          :images="object.images"
          :videos="object.videos"
          :video="ziVideo"
          :download="object.name"
          :alt="`${object.name} images`"
          include-small
          zoom
        />
        <div class="content">
          <h1 class="product-name">
            {{ shop.getProductNameWithoutColor(object.name) }}
          </h1>
          <div class="description" v-html="description" />
          <div v-if="ziItem?.sigDark" class="signature m-xs-y">
            <img v-bind="ziItem.sigDark" sizes="200px" alt="Zi Signature" />
          </div>
          <ul
            v-if="categoryLabels && categoryLabels.length > 0"
            class="categories"
          >
            <li v-for="category of categoryLabels" class="category-space">
              {{ category }}
            </li>
          </ul>
          <div v-if="user.isRep" class="price-row">
            <template v-if="!object.is_parent">
              <span v-for="(amount, group) in object.prices" class="price">
                <span class="price-group" v-if="group === 'null'">
                  Retail:
                </span>
                <span class="price-group" v-else>{{ group }}:</span>
                <Currency v-if="amount" :amount="amount" />
              </span>
              <span class="price">
                <span class="price-group">Volume:</span>
                <span>{{ parseFloat(object.volume).toFixed(0) }}</span>
              </span>
            </template>
          </div>
          <div v-else class="price-row">
            <ProductPrice
              v-if="!object.is_parent"
              :object="object"
              class="price"
            />
          </div>
          <div class="add-row flex-row flex-align-center">
            <AddToCart expanded :object="object" />
          </div>
          <div v-if="!shop.isApp && isBlockbuster" class="blockbuster-link">
            To see our 2023 Life of the party Blockbuster Models, click
            <RouterLink :to="{ name: 'lop' }" class="pa-pink">here</RouterLink>!
          </div>
          <div class="remote-id">{{ object.remote_id }}</div>

          <div v-if="user.isRep" class="flex-column p-md-t gap-xs">
            <div class="flex-row gap-xmd">
              <a
                onclick="return !window.open(this.href, 'Facebook', 'width=640,height=300')"
                :href="`http://www.facebook.com/sharer/sharer.php?u=${replicatedProductURL}&p[images][0]=${productImageURL}`"
                target="_blank"
                rel="noopener"
              >
                <i class="pi pi-facebook" />
              </a>
              <a
                onclick="return !window.open(this.href, 'Twitter', 'width=640,height=300')"
                :href="`https://twitter.com/share?text=${object.name}&url=${replicatedProductURL}`"
                target="_blank"
                rel="noopener"
              >
                <i class="pi pi-twitter" />
              </a>
              <a
                onclick="return !window.open(this.href, 'Pinterest', 'width=640,height=300')"
                :href="`http://pinterest.com/pin/create/link?url=${replicatedProductURL}&media=${productImageURL}`"
                target="_blank"
                rel="noopener"
              >
                <i class="pi pi-pinterest" />
              </a>
              <a
                onclick="return !window.open(this.href, 'Pinterest', 'width=640,height=300')"
                :href="`mailto:?subject=${object.name}&body=${replicatedProductURL} - ${productImageURL}`"
                target="_blank"
                rel="noopener"
              >
                <i class="pi pi-envelope" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <RelatedProductsList :object="object" :extended="true" />
    <div class="footer" />
  </div>
  <div v-else-if="!shop.isReady || status">
    <div v-if="status" class="flex-row flex-center align-center p-sm">
      {{ status }}
    </div>
  </div>
  <div v-else>
    <div class="flex-row flex-center align-center p-sm">
      <h1>Product Not Found</h1>
    </div>
  </div>
</template>

<route lang="json">
{
  "name": "product-detail",
  "meta": { "fullWidth": true }
}
</route>

<script setup lang="ts">
import RepBanner from "@/components/rep/RepBanner.vue";
import AddToCart from "@/components/shop/AddToCart.vue";
import Currency from "@/components/shop/Currency.vue";
import ImageGallery from "@/components/shop/ImageGallery.vue";
import ProductPrice from "@/components/shop/ProductPrice.vue";
import RelatedProductsList from "@/components/shop/RelatedProductsList.vue";
import { type Category, type Product } from "@/stores/defs/shop_defs";
import { useShopStore } from "@/stores/shop";
import { useUserStore } from "@/stores/user";
import { useZiStore } from "@/stores/zi";
import { type ZiItem } from "@/stores/zi/zi_defs";
import { getCategoryLabels } from "@/utils/shop";
import { expandThumb } from "@paparazzi/components/stores/thumbnail_conversion";
import { nextFrame, timer } from "@paparazzi/utils/promise";
import { useHead } from "@unhead/vue";
import api from "@virgodev/bazaar/functions/api";
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";

const props = defineProps<{
  slug: string;
  consultantId?: string;
}>();

const route = useRoute();
const shop = useShopStore();
const user = useUserStore();
const zi = useZiStore();

const attemptedRemote = ref(false);
const status = ref("");
const remoteObject = ref<Product | undefined>();

const object = computed(() => remoteObject.value || allowedObject.value);

const replicatedProductURL = computed(
  () =>
    `https://paparazziaccessories.com/shop/products/${object.value.slug}/${user.props.rep_number}/`,
);
const productImageURL = computed(() => {
  if (object.value?.images && object.value.images.length > 0) {
    return expandThumb(object.value.images[0].w500);
  }
  return "";
});

const allowedObject = computed<Product | undefined>(() => {
  return shop.allowedProducts.find((p) => p.slug === props.slug);
});

const allObject = computed<Product | undefined>(() => {
  return shop.objects.products.find((p) => p.slug === props.slug);
});

const allCategories = computed(() => {
  return shop.allowedCategories.filter((category) => {
    return (object.value?.categories || []).includes(category.id);
  });
});

const categories = computed(() => {
  return shop.objects.categories.filter((category) => {
    return (object.value?.categories || []).includes(category.id);
  });
});

const allowed = computed(() => {
  return (
    allowedObject.value ||
    (categories.value.length > 0 &&
      categories.value.length === allCategories.value.length)
  );
});

const categoryLabels = computed(() => {
  if (object.value) {
    return getCategoryLabels(object.value);
  }
  return [];
});

const description = computed(() => {
  if (object.value) {
    let desc = object.value.description;
    desc = desc.replace(/<img .*\/?>/g, (a) => {
      return "";
    });
    return desc;
  } else {
    return "";
  }
});

const ziItem = computed<ZiItem>(() => {
  if (object.value) {
    return zi.list.find((i) => i.sku === object.value?.remote_id);
  }
  return undefined;
});
const ziVideo = computed<undefined | { source: string; thumb: any }>(() => {
  if (ziItem.value) {
    return {
      source: ziItem.value.video,
      thumb: ziItem.value.videoThumb,
    };
  }
});
const blockbusterParent = computed<Category | undefined>(() => {
  return shop.categories.find((i) => i.name === "Blockbusters");
});
const isBlockbuster = computed<boolean>(() => {
  for (const cat of categories.value) {
    if (cat.parent === blockbusterParent.value?.id) {
      return true;
    }
  }
  return false;
});

watch(() => props.slug, loadRemote);

useHead({
  title: () => {
    if (object.value?.name) {
      return object.value.name;
    }
    if (!status.value && shop.isReady) {
      return "Product Not Found";
    }
    return "Loading product...";
  },
  meta: () => {
    if (object.value) {
      const meta = [
        { name: "og:title", content: object.value.name },
        { name: "og:description", content: object.value.description },
        { name: "og:url", content: route.fullPath },
      ];
      if (object.value.images.length > 0) {
        meta.push({
          name: "og:image",
          content: object.value.images[0].original,
        });
      }
    }
    return [];
  },
});

onMounted(async () => {
  const timeout = user.isLocalhost ? 0 : 10;
  await shop.ready;

  if (!allowedObject.value && allObject.value) {
    // attempting to load a not allowed product
    status.value = "Loading...";
    await longwait(timeout);
    status.value = "";
  } else if (!allowedObject.value) {
    status.value = "Loading...";
    await longwait(timeout);
    await loadRemote();
    await nextTick();
    if (!allowed.value) {
      remoteObject.value = undefined;
    }
    status.value = "";
  }
  zi.setup();

  if (props.consultantId) {
    user.changeRep(props.consultantId);
  }
});

async function longwait(seconds: number) {
  while (seconds > 0) {
    await timer(1000 + Math.random() * seconds);
    // must be looking at the tab to proceed
    await nextFrame();
    seconds -= 1;
  }
}

async function loadRemote() {
  attemptedRemote.value = true;
  remoteObject.value = undefined;
  await nextTick();
  if (!object.value) {
    const response = await api({ url: `products/${props.slug}/` });
    if (response.ok) {
      remoteObject.value = response.body;
    }
  }
}
</script>

<style scoped>
.page {
  margin-bottom: 0;
}
.back-row a {
  color: var(--50, #939598);
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  text-transform: uppercase;
}
.product-content {
  gap: var(--1XL, 64px);
}
.product-detail {
  max-width: 928px;
  padding: 0 var(--Sm, 16px);
  margin: auto;
  gap: var(--XL, 56px);
}
.product-detail.flex-row {
  gap: 40px;
}
.product-detail > * {
  flex: 1 1 1px;
}
.product-detail {
  :deep(.img-detail) {
    margin-left: -24px;
    width: 418px;
  }
}
h1 {
  color: var(--Black, #000);
  font-family: Indivisible;
  font-size: 24px;
  font-style: normal;
  font-weight: 300;
  line-height: normal;
  text-align: left;
  text-transform: none;
  margin: 0 0 var(--XS, 16px) 0;
}

h1.product-name {
  font-weight: 300;
  text-shadow: none;
}

/*
  h2.product-name shouldn't be bold
  isn't a link
*/

.content {
  min-width: 430px;
  letter-spacing: 0.25px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-left: 20px;
}
.description {
  color: var(--Black, #000);
  font-family: Indivisible;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  margin: 0;
  padding-bottom: var(--XMd, 40px);
}

.content:deep(.description p) {
  margin-top: 0;
}
.content:deep(.description p:last-child) {
  margin-bottom: 0;
}

ul.categories {
  color: var(--Black, #000);
  margin: 0 0 var(--XMd, 40px) 0;
  display: inline;
  flex-direction: column;
  gap: 8px;
  list-style: none;
  /* Paragraph 1 */
  font-family: Indivisible;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  padding: 0;
  li.category-space {
    padding: 0 var(--XS, 16px) 0 var(--XS, 16px);
    float: left;
    border-right: 1px solid var(--50, #939598);
  }
  .category-space:first-child {
    padding-left: 0;
  }
  .category-space:last-child {
    padding-right: 0;
    border-right: none;
  }
}
.main-image {
  width: 100%;
  height: 432px;
  aspect-ratio: 8 / 10;
  object-fit: cover;
}
.price-row {
  padding-bottom: var(--XS, 16px);
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}
.price-group {
  text-transform: capitalize;
  margin-right: 5px;
  display: inline-block;
}
.add-row {
  display: flex;
  flex-direction: column;
  gap: var(--XS, 16px);
  margin-bottom: var(--md, 32px);
}
.price {
  color: var(--Black, #000);

  /* Subhead 1 */
  font-family: Indivisible;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}
.remote-id {
  color: var(--Black, #000);

  /* Small Print */
  font-family: Indivisible;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}

.blockbuster-link {
  margin-bottom: var(--Sm);
}

@media (max-width: 930px) {
  .product-detail {
    max-width: 100%;
  }
  .content {
    min-width: auto;
  }
}
@media (max-width: 700px) {
  .product-detail {
    display: block;
  }
  .product-detail .flex-row {
    flex-direction: column !important;
  }
  .product-detail :deep(.img-detail) {
    margin: 0 auto !important;
    width: 395px;
  }
  .add-row.flex-row {
    flex-direction: row !important;
  }
  .content {
    margin-top: var(--XL, 56px);
    padding-left: 0;
  }
  .description {
    margin: 0;
  }
  .product-content {
    gap: var(--XMd, 40px);
  }
  .related-products :deep(h4) {
    text-align: left;
  }
  .product-list {
    grid-template-columns: repeat(auto-fit, minmax(167px, auto));
  }
  .product-detail :deep(.small) {
    margin: 24px auto;
  }
}
</style>
